import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, KeyRound, ArrowLeft, AlertCircle } from "lucide-react";
import { Logo } from "@/components/Shared";
import { usePageTitle } from "@/hooks/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white placeholder:text-slate-500 focus:border-blue-500 focus:outline-none";
const labelCls = "text-xs font-bold uppercase tracking-wider text-slate-400";

const AcceptInvitation = () => {
  const { token } = useParams();
  usePageTitle("Invitation");
  const [invite, setInvite] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/auth/invitation/${token}`)
      .then((res) => setInvite(res.data))
      .catch(() => setInvalid(true));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/accept-invitation`, { token, password });
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="invitation-page"
      className="relative flex min-h-screen items-center justify-center bg-[#0A0F1D] px-5"
    >
      <div className="pointer-events-none absolute inset-0 bg-grid-navy" />
      <div className="pointer-events-none absolute -top-24 left-1/3 h-96 w-96 rounded-full bg-blue-600/15 blur-3xl" />
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-9 backdrop-blur-xl">
        <Logo dark className="h-9" />

        {invalid && (
          <div className="mt-8" data-testid="invitation-invalid">
            <p className="flex items-center gap-2 rounded-lg bg-rose-500/10 px-4 py-3 text-sm font-medium text-rose-400">
              <AlertCircle className="h-4 w-4" /> Ce lien d'invitation est invalide ou expiré.
            </p>
            <Link
              to="/admin/login"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" /> Aller à la connexion
            </Link>
          </div>
        )}

        {invite && !done && (
          <>
            <h1 className="mt-8 font-display text-2xl font-bold text-white">
              Bienvenue {invite.name ? `${invite.name} ` : ""}!
            </h1>
            <p className="mt-2 text-sm text-slate-400">
              Créez votre mot de passe pour activer votre accès administrateur (
              <span className="font-semibold text-slate-300">{invite.email}</span>).
            </p>
            <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="invitation-form">
              <div className="space-y-2">
                <label className={labelCls}>Mot de passe</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="invitation-password-input"
                  className={inputCls}
                  placeholder="8 caractères minimum"
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <label className={labelCls}>Confirmer le mot de passe</label>
                <input
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="invitation-confirm-input"
                  className={inputCls}
                  autoComplete="new-password"
                />
              </div>
              {error && (
                <p data-testid="invitation-error" className="rounded-lg bg-rose-500/10 px-4 py-3 text-sm font-medium text-rose-400">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="invitation-submit"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-blue-500 disabled:opacity-60"
              >
                <KeyRound className="h-4 w-4" />
                {loading ? "Activation…" : "Activer mon accès"}
              </button>
            </form>
          </>
        )}

        {done && (
          <div className="mt-8" data-testid="invitation-success">
            <p className="flex items-center gap-2 rounded-lg bg-emerald-500/10 px-4 py-3 text-sm font-medium text-emerald-400">
              <CheckCircle2 className="h-4 w-4" /> Votre accès est activé !
            </p>
            <Link
              to="/admin/login"
              data-testid="invitation-login-link"
              className="mt-6 flex w-full items-center justify-center rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-blue-500"
            >
              Se connecter au back office
            </Link>
          </div>
        )}
      </div>
    </main>
  );
};

export default AcceptInvitation;
